import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as LockedIcon } from '../../../assets/icons/access/locked.svg';
import { ReactComponent as NoAccessIcon } from '../../../assets/icons/access/no-access.svg';
import AppUserAvatar from '../../../components/designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import {
  AcceptSubscriptionRequestButton,
  CancelSubscriptionRequestButton,
  DropFromSubscribersButton,
  RejectSubscriptionRequestButton,
  RequestSubscriptionButton,
  SubscribeButton,
  UnsubscribeButton,
} from '../../../components/designe/buttons/subscription-buttons';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import CommunitiesListSmall from '../../../components/reusable/communities-list/CommunitiesListSmall';
import LoadMoreFeedback from '../../../components/reusable/load-more-feedback/LoadMoreFeedback';
import NoResults from '../../../components/reusable/no-results';
import Posts from '../../../components/reusable/posts/Posts';
import UsersListSmall from '../../../components/reusable/users-list/UsersListSmall';
import withRouter from '../../../hoc/with-router';
import {
  AvatarSize,
  AvatarTypes,
  RelationStatus,
} from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import UserProfileSkeleton from '../shared/user-profile-skeleton/UserProfileSkeleton';
import { useStyles } from './User.styles';
import { useUserPage } from './useUserPage';

function User(props) {
  const { params } = props;
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const {
    isRelationPanel,
    isCurrentUser,
    posts,
    reviewedUser,
    footerRef,
    reviewedUserSubscriptions,
    reviewedUserSubscribers,
    communities,
    isUserLoading,
    showNoResult,
    showNoDetailsAccess,
    showNoPostsAccess,
    showPrivateSubscribeAction,
    privatePostsSubscribeLabelKey,
    noAccessTitleKey,
    noAccessDescriptionKey,
    visibleRelationsCount,
    isReviewedUserError,
    reviewedUserError,
    onSubscribe,
    onUnsubscribe,
    isSubscribeLoading,
    isUnsubscribeLoading,
    isApproveStatusLoading,
    isRejectStatusLoading,
    isDropStatusLoading,
    onSubscriptionStatusChange,
    refetchAll,
    isFetchingPosts,
    postsError,
  } = useUserPage(params.userId);

  useRegisterPageContainerError({
    errors: isReviewedUserError && reviewedUserError ? [reviewedUserError] : [],
    isLoading: isUserLoading,
    hasData: Boolean(reviewedUser),
    onRetry: refetchAll,
  });

  if (isUserLoading) {
    return (
      <PageContainer
        backButton={true}
        title={t('users.user')}
        onRefresh={refetchAll}>
        <UserProfileSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      backButton={true}
      title={t('users.user')}
      onRefresh={refetchAll}>
      <div className={classes.userInfoWrapper}>
        <div className={classes.avatar}>
          <AppUserAvatar
            entity={reviewedUser}
            size={AvatarSize.l}
            type={AvatarTypes.round}
          />
        </div>
        <div className={classes.userDetails}>
          <div className={classes.info}>
            <Typography variant="text-lg">{`${reviewedUser?.firstname || ''} ${reviewedUser?.lastname || ''}`}</Typography>
            {!showNoDetailsAccess && (
              <Typography variant="text-md" className={classes.description}>
                {reviewedUser?.description}
              </Typography>
            )}
            {showNoDetailsAccess && (
              <Box className={classes.lockedProfileInfo}>
                <LockedIcon className={classes.lockedProfileIcon} />
                <Typography variant="text-md" className={classes.noAccessText}>
                  Закрытый профиль
                </Typography>
              </Box>
            )}
            {!showNoDetailsAccess && (
              <Box className={classes.stats}>
                <Typography variant="text-md" component="span">
                  <Typography component="span" className={classes.statsValue}>
                    {reviewedUserSubscribers?.length ?? 0}
                  </Typography>{' '}
                  {t('users.subscribers')}
                </Typography>
                <Typography variant="text-md" component="span">
                  <Typography component="span" className={classes.statsValue}>
                    {reviewedUserSubscriptions?.length ?? 0}
                  </Typography>{' '}
                  {t('users.subscriptions')}
                </Typography>
              </Box>
            )}
          </div>
          {!isCurrentUser && (
            <div className={classes.actions}>
              {reviewedUser?.permissions?.canSubscribe && (
                <SubscribeButton
                  onClick={() => onSubscribe()}
                  loading={isSubscribeLoading}
                />
              )}
              {reviewedUser?.permissions?.canRequestSubscription && (
                <RequestSubscriptionButton
                  onClick={() => onSubscribe()}
                  loading={isSubscribeLoading}
                />
              )}
              {reviewedUser?.permissions?.canUnsubscribe && (
                <UnsubscribeButton
                  onClick={() => onUnsubscribe()}
                  loading={isUnsubscribeLoading}
                />
              )}
              {reviewedUser?.permissions?.canCancelRequestSubscription && (
                <CancelSubscriptionRequestButton
                  onClick={() => onUnsubscribe()}
                  loading={isUnsubscribeLoading}
                />
              )}
              {reviewedUser?.permissions?.canApproveRequest && (
                <AcceptSubscriptionRequestButton
                  loading={isApproveStatusLoading}
                  onClick={() =>
                    onSubscriptionStatusChange(
                      RelationStatus.approved,
                      'approve',
                    )
                  }
                />
              )}
              {reviewedUser?.permissions?.canRejectRequest && (
                <RejectSubscriptionRequestButton
                  loading={isRejectStatusLoading}
                  onClick={() =>
                    onSubscriptionStatusChange(
                      RelationStatus.rejected,
                      'reject',
                    )
                  }
                />
              )}
              {reviewedUser?.permissions?.canDropFromSubscribers && (
                <DropFromSubscribersButton
                  loading={isDropStatusLoading}
                  onClick={() =>
                    onSubscriptionStatusChange(RelationStatus.rejected, 'drop')
                  }
                />
              )}
              {/*<AppButton onClick={() => onSendMessageClick()}>*/}
              {/*  {t('users.sendMessage')}*/}
              {/*</AppButton>*/}
            </div>
          )}
        </div>
      </div>
      {showNoPostsAccess && (
        <Box className={classes.privatePostsContainer}>
          <Box className={classes.privatePostsIconWrapper}>
            <NoAccessIcon className={classes.privatePostsIcon} />
          </Box>
          <Typography variant="h2" className={classes.privatePostsTitle}>
            {t(noAccessTitleKey)}
          </Typography>
          <Typography
            variant="text-md"
            className={classes.privatePostsDescription}>
            {t(noAccessDescriptionKey)}
          </Typography>
          {showPrivateSubscribeAction && (
            <SubscribeButton
              labelKey={privatePostsSubscribeLabelKey}
              onClick={() => onSubscribe()}
              loading={isSubscribeLoading}
            />
          )}
        </Box>
      )}
      {reviewedUser?.permissions?.canSeePosts && (
        <Box className={classes.userDetailsWrapper}>
          <Box className={classes.userPostsWrapper}>
            {showNoResult && <NoResults />}
            {!showNoResult && (
              <Posts
                posts={posts}
                isLoading={isFetchingPosts}
                refreshPosts={refetchAll}
                newPostBinding={{ pageUserId: reviewedUser?.id }}
                allowNewPost={reviewedUser?.permissions?.canAddPost}
              />
            )}
            <LoadMoreFeedback
              error={postsError}
              isFetching={Boolean(isFetchingPosts) && Boolean(posts?.length)}
              onRetry={refetchAll}
            />
            <Box ref={footerRef} mb={1} />
          </Box>
          {!!isRelationPanel && (
            <Box className={classes.userRelationsWrapper}>
              {!!reviewedUserSubscriptions?.length && (
                <Box className={classes.userRelationsContainer}>
                  <Link
                    className={classes.userRelationsHeader}
                    to={Paths.users.toUserSubscriptions(reviewedUser?.id)}>
                    <Typography variant="h3" marginBottom={1}>
                      {t('users.userSubscriptions')}
                      <Typography
                        component="span"
                        className={classes.userRelationsCounter}>
                        {reviewedUserSubscriptions.length}
                      </Typography>
                    </Typography>
                  </Link>
                  <UsersListSmall
                    users={reviewedUserSubscriptions?.slice(
                      0,
                      visibleRelationsCount,
                    )}
                  />
                </Box>
              )}
              {!!reviewedUserSubscribers?.length && (
                <Box className={classes.userRelationsContainer}>
                  <Link
                    className={classes.userRelationsHeader}
                    to={Paths.users.toUserSubscribers(reviewedUser?.id)}>
                    <Typography variant="h3" marginBottom={1}>
                      {t('users.userSubscribers')}
                      <Typography
                        component="span"
                        className={classes.userRelationsCounter}>
                        {reviewedUserSubscribers.length}
                      </Typography>
                    </Typography>
                  </Link>
                  <UsersListSmall
                    users={reviewedUserSubscribers?.slice(
                      0,
                      visibleRelationsCount,
                    )}
                  />
                </Box>
              )}
              {!!communities?.length && (
                <Box className={classes.userRelationsContainer}>
                  <Link
                    className={classes.userRelationsHeader}
                    to={Paths.users.toUserCommunities(reviewedUser?.id)}>
                    <Typography variant="h3" marginBottom={1}>
                      {t('home.myCommunities')}
                      <Typography
                        component="span"
                        className={classes.userRelationsCounter}>
                        {communities.length}
                      </Typography>
                    </Typography>
                  </Link>
                  <CommunitiesListSmall
                    communities={communities?.slice(0, visibleRelationsCount)}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </PageContainer>
  );
}

export default withRouter(User);
